import companySignalTriggerLibrary from './companySignalTriggerLibrary.json';

export const DEFAULT_SIGNAL_TRIGGER_CATEGORIES = [
  ...companySignalTriggerLibrary,

  // --- Industry Level Triggers (3 Categories) ---
  {
    id: "industry_market_dynamics",
    type: "industry",
    name: "Market Dynamics & Macroeconomics",
    icon: "Globe",
    description: "Broad economic cycles, sector consolidation, and global market trends.",
    signals: [
      {
        id: "economic_shifts",
        name: "Economic Shifts",
        description: "Recessions, inflation, or market downturns.",
        sources: ["Federal Reserve", "Financial News", "Bloomberg"],
        enabled: true,
      },
      {
        id: "industry_consolidation",
        name: "Industry Consolidation",
        description: "Fewer, larger players through mergers and exits.",
        sources: ["Trade Journals", "Market Reports"],
        enabled: true,
      },
      {
        id: "global_trade_changes",
        name: "Global Trade Changes",
        description: "Tariffs, trade agreements, or cross-border impacts.",
        sources: ["Trade Publications", "Government Gazettes"],
        enabled: false,
      },
      {
        id: "emerging_markets",
        name: "Emerging Markets Expansion",
        description: "Growth focus on emerging economies.",
        sources: ["World Bank", "Sector Whitepapers"],
        enabled: false,
      },
      {
        id: "market_expansion_ind",
        name: "Market Expansion",
        description: "Competitors or peers entering new geographies.",
        sources: ["Industry Watch", "News & PR"],
        enabled: false,
      },
      {
        id: "supply_chain_disruptions",
        name: "Supply Chain Disruptions",
        description: "Logistics or supplier issues impacting the industry.",
        sources: ["Logistics Briefs", "Reuters"],
        enabled: false,
      },
      {
        id: "pricing_model_changes",
        name: "Pricing Model Changes",
        description: "Subscription, usage-based, or other pricing shifts.",
        sources: ["Industry Pricing Index", "SaaS Reports"],
        enabled: false,
      },
    ],
  },
  {
    id: "industry_regulation_compliance",
    type: "industry",
    name: "Regulation & Compliance",
    icon: "ShieldAlert",
    description: "New compliance mandates, privacy policies, and industry standards.",
    signals: [
      {
        id: "regulatory_changes",
        name: "Regulatory Changes",
        description: "New compliance requirements in the industry.",
        sources: ["Regulatory Bulletins", "Legal Alerts"],
        enabled: true,
      },
      {
        id: "compliance_updates",
        name: "Compliance Updates",
        description: "New standards or audits affecting the industry.",
        sources: ["ISO / SOC Standards", "Audit Reports"],
        enabled: false,
      },
      {
        id: "data_privacy_regulations",
        name: "Data Privacy Regulations",
        description: "Privacy laws shaping how data is handled.",
        sources: ["GDPR / CCPA Portals", "Privacy Boards"],
        enabled: false,
      },
      {
        id: "policy_changes",
        name: "Policy Changes",
        description: "Government or trade policy updates affecting the sector.",
        sources: ["Policy Briefs", "Trade Associations"],
        enabled: false,
      },
      {
        id: "sustainability_initiatives",
        name: "Sustainability Initiatives",
        description: "ESG and green initiatives gaining traction.",
        sources: ["ESG Rating Agencies", "Sustainability Reports"],
        enabled: false,
      },
    ],
  },
  {
    id: "industry_technology_innovation",
    type: "industry",
    name: "Technology & Innovation",
    icon: "Zap",
    description: "Industry-wide digital evolution, AI adoption, and product disruption.",
    signals: [
      {
        id: "ai_adoption",
        name: "AI Adoption",
        description: "Accelerating use of AI across the industry.",
        sources: ["TechCrunch", "Gartner Reports", "Industry Benchmarks"],
        enabled: true,
      },
      {
        id: "tech_trends",
        name: "Technological Trends",
        description: "Growth in AI, cloud adoption, or automation.",
        sources: ["Forrester", "IDC Research"],
        enabled: false,
      },
      {
        id: "cloud_migration",
        name: "Cloud Migration Trends",
        description: "Move to cloud infrastructure across the industry.",
        sources: ["AWS / Azure Case Studies", "Cloud Reports"],
        enabled: false,
      },
      {
        id: "automation_adoption",
        name: "Automation Adoption",
        description: "Broader rollout of process automation.",
        sources: ["Automation Forums", "Workflow Benchmarks"],
        enabled: false,
      },
      {
        id: "new_product_launches_ind",
        name: "New Product Launches",
        description: "Industry players releasing new offerings.",
        sources: ["Press Releases", "Product Showcases"],
        enabled: false,
      },
      {
        id: "digital_transformation",
        name: "Digital Transformation",
        description: "Industry shift toward digital-first operations.",
        sources: ["McKinsey Insights", "Digital Index"],
        enabled: false,
      },
      {
        id: "security_breaches_ind",
        name: "Security Breaches",
        description: "High-profile security incidents in the sector.",
        sources: ["Cyber Threat Feeds", "CERT Alerts"],
        enabled: false,
      },
      {
        id: "industry_events_ind",
        name: "Industry Events",
        description: "Major conferences, summits, or trade shows.",
        sources: ["Conference Calendars", "Trade Press"],
        enabled: false,
      },
    ],
  },
];

const normalize = (value) => String(value ?? "").trim().toLowerCase();
const LEGACY_SIGNAL_ALIASES = {
  growth_strategy: "aggressive_growth",
  "growth strategy": "aggressive_growth",
  expansion: "geo_expansion",
  sales_targets: "rev_growth_targets",
  "sales targets/vision": "rev_growth_targets",
  "product or service launch": "product_launch",
  "funding rounds": "funding_rounds",
  "new executive hires": "new_exec_hires",
  "promotions or role changes": "promotions_roles",
  "layoffs or restructuring": "layoffs_restructuring",
  "tech stack changes": "tech_stack_changes",
  "mergers & acquisitions (m&a)": "m_and_a",
  partnerships: "partnerships",
  "infrastructure downtime": "infrastructure_downtime",
  "customer churn": "customer_churn",
  rfp_announcements: "company_signal_published_rfp_or_tender",
  "rfp announcements": "company_signal_published_rfp_or_tender",
  negative_press: "company_signal_negative_press_on_operations_or_customers",
  "negative press": "company_signal_negative_press_on_operations_or_customers",
};

function normalizeSignal(signal) {
  return {
    ...signal,
    id: signal.id || `sig_${crypto.randomUUID()}`,
    name: signal.name || signal.label || "",
    description: signal.description || "",
    sources: Array.isArray(signal.sources) ? [...new Set(signal.sources)] : [],
    enabled: Boolean(signal.enabled),
  };
}

export function mergeSignalConfig(existingConfig = null, companyLevelTriggers = [], industryLevelTriggers = []) {
  const categories = DEFAULT_SIGNAL_TRIGGER_CATEGORIES.map((category) => ({
    ...category,
    selected: false,
    signals: category.signals.map((signal) => ({ ...normalizeSignal(signal), enabled: false })),
  }));
  const findSignal = (type, value) => {
    const key = normalize(value);
    const alias = type === "company" ? LEGACY_SIGNAL_ALIASES[key] : null;
    for (const category of categories.filter((item) => item.type === type)) {
      const signal = category.signals.find((item) =>
        normalize(item.id) === (alias || key) || normalize(item.name) === key,
      );
      if (signal) return { category, signal };
    }
    return null;
  };

  if (Array.isArray(existingConfig)) {
    existingConfig.forEach((savedCategory) => {
      // A default category keeps its current group even if it was saved under another one.
      const defaultType = categories.find((category) => category.id === savedCategory.id)?.type;
      const type = defaultType || (savedCategory.type === "industry" ? "industry" : "company");
      let matchingCategory = categories.find((category) => category.id === savedCategory.id && category.type === type);
      const selected = savedCategory.selected ?? (savedCategory.signals || []).some((signal) => signal.enabled);
      if (!matchingCategory && !savedCategory.signals?.length) {
        matchingCategory = { ...savedCategory, type, id: savedCategory.id || `category_${crypto.randomUUID()}`, selected: Boolean(selected), signals: [] };
        categories.push(matchingCategory);
      }
      if (matchingCategory) matchingCategory.selected = Boolean(selected);
      (savedCategory.signals || []).forEach((rawSignal) => {
        const savedSignal = normalizeSignal(rawSignal);
        const match = findSignal(type, savedSignal.id) || findSignal(type, savedSignal.name);
        if (match) {
          const wasEnabled = match.signal.enabled;
          const canonicalName = match.signal.name;
          const canonicalId = match.signal.id;
          Object.assign(match.signal, savedSignal, {
            id: canonicalId,
            name: LEGACY_SIGNAL_ALIASES[normalize(savedSignal.name)] ? canonicalName : savedSignal.name,
            enabled: Boolean(selected) && (wasEnabled || savedSignal.enabled),
          });
          if (selected && (matchingCategory === match.category || savedSignal.enabled)) match.category.selected = true;
        } else {
          if (!matchingCategory) {
            matchingCategory = {
              ...savedCategory,
              type,
              id: savedCategory.id || `category_${crypto.randomUUID()}`,
              selected: Boolean(selected),
              signals: [],
            };
            categories.push(matchingCategory);
          }
          const duplicate = matchingCategory.signals.find((signal) => normalize(signal.name) === normalize(savedSignal.name));
          if (!duplicate) matchingCategory.signals.push({ ...savedSignal, enabled: Boolean(selected) && savedSignal.enabled });
        }
      });
      if (matchingCategory) {
        const savedOrder = new Map((savedCategory.signals || []).map((signal, index) => [
          (findSignal(type, signal.id) || findSignal(type, signal.name))?.signal.id || signal.id,
          index,
        ]));
        matchingCategory.signals.sort((a, b) =>
          (savedOrder.get(a.id) ?? Infinity) - (savedOrder.get(b.id) ?? Infinity),
        );
      }
    });
    return categories;
  }

  [["company", companyLevelTriggers], ["industry", industryLevelTriggers]].forEach(([type, triggers]) => {
    (Array.isArray(triggers) ? triggers : []).forEach((item) => {
      const value = typeof item === "string" ? item : item?.name || item?.value;
      if (!normalize(value)) return;
      const match = findSignal(type, value);
      if (match) {
        match.category.selected = true;
        match.signal.enabled = true;
      } else {
        const category = categories.find((entry) => entry.type === type);
        if (!category.signals.some((signal) => normalize(signal.name) === normalize(value))) {
          category.signals.push(normalizeSignal({
            name: String(value).trim(),
            description: typeof item === "object" ? item.description : "",
            sources: typeof item === "object" ? item.sources : [],
            enabled: true,
          }));
        }
        category.selected = true;
      }
    });
  });
  return categories;
}

export function extractEnabledTriggers(categories = []) {
  const activeNames = (type) => Array.from(new Set(
    categories.filter((category) => category.type === type && category.selected !== false)
      // A category with no triggers of its own is itself the trigger.
      .flatMap((category) => category.signals?.length ? category.signals : [{ name: category.name ?? "", enabled: true }])
      .filter((signal) => signal.enabled && signal.name.trim())
      .map((signal) => signal.name.trim()),
  ));
  return {
    companyLevelSalesTrigger: activeNames("company"),
    industryLevelTrigger: activeNames("industry"),
    signalTriggerConfig: categories,
  };
}
